import type { AudioChannel, SpatialType } from './audioManager';
import type { StateTag } from '../scene/tagging';
import type { StateConfig } from '../state/stateConfig';

// A scheduled sound in the era × time-of-day "score". For Stage 1 the engine
// consumes two sources: BEDS + MUSIC derived from states.json (below), and
// one-shots from audio-cues.json (the CUES lane). Stage 2 unifies authoring of
// all of these onto the timeline + a single soundscape file; this type is the
// shape they converge on.
// Three citizens (N1): beds are weather (continuous, level-triggered), cues
// are lightning (instant, edge-triggered — they live in `cues`, not here),
// CLIPS are speech — regions with real durations, aligned to the clock like a
// DAW region. Music is the diegetic boombox slot.
export type PlacementKind = 'bed' | 'music' | 'clip';

// Per-trigger variation — "many days from one". Applied by the SoundscapeEngine
// each time a bed (re)starts or a cue fires, so a single authored entry never
// plays exactly the same way twice:
//   chance   probability (0..1) it plays at all this trigger (default 1).
//   oneOf    asset pool; pick one at random per trigger (overrides `asset`).
//   detune   ± cents; a random offset in [-detune, detune] per instance.
//   jitterSec  cues only: random 0..jitterSec wall-clock delay before firing.
export interface Variation {
  chance?: number;
  oneOf?: string[];
  detune?: number;
  jitterSec?: number;
}

// Continuous bed modulation (Stage 4) — makes a held loop breathe + evolve
// instead of sitting at a flat level:
//   depth      0..1 gain LFO depth; level dips to (1-depth) at the trough.
//   rateSec    LFO period in seconds (default ~6).
//   reshuffleSec  if set (and the bed has a oneOf pool), re-pick the variant
//                 this often — loop-to-loop texture (a slow crossfade shuffle).
export interface Mod {
  depth?: number;
  rateSec?: number;
  reshuffleSec?: number;
}

// A bed "drop" (A12) — one member of a bed's population. The bed's presence is
// guaranteed (its optional base loop); inside it, each drop fires a one-shot at
// a staggered random interval — the street is always there, but WHICH
// motorcycle and WHEN the voices is per-fire weather:
//   oneOf   one-shot asset pool; one picked at random per fire.
//   every   [min,max] seconds between fires (real audio-clock seconds).
//   chance  per-fire probability (default 1) — a miss still consumes the slot.
//   detune  ± cents jitter per fire.
//   gain    fixed level or [min,max] range per fire; era weight scales it.
//   at      optional anchor override (defaults to the bed's anchor).
export interface Drop {
  oneOf: string[];
  every: [number, number];
  chance?: number;
  detune?: number;
  gain?: number | [number, number];
  at?: { heroId?: string; position?: [number, number, number] };
}

export interface SoundPlacement {
  id: string;
  kind: PlacementKind;
  /** Base loop. Optional for beds that are pure drop populations (A12);
   *  required for music placements. */
  asset?: string;
  /** Era gate; 'both' (or undefined) = present in either era. */
  scope: StateTag;
  channel: AudioChannel;
  at?: { heroId?: string; position?: [number, number, number] };
  spatial?: SpatialType;
  /** Spatial falloff overrides in metres (point/area sources only). When set,
   *  they override the spatial-type preset and are visualized as the source +
   *  falloff wireframe spheres:
   *    refDistance  inner "full-presence" core radius.
   *    maxDistance  outer audible-reach radius.
   *  Authored in the bed editor; persisted so the spatial mix is data-driven. */
  refDistance?: number;
  maxDistance?: number;
  /** Base volume 0..1. */
  gain?: number;
  /** Bed active window in the day [start,end]; default = all day. */
  span?: [number, number];
  loop?: boolean;
  /** Per-(re)start variation (beds use chance/oneOf/detune). */
  vary?: Variation;
  /** Continuous modulation while held (beds): gain LFO + loop-to-loop reshuffle. */
  mod?: Mod;
  /** Bed population members (A12) — one-shots fired at staggered random
   *  intervals while the bed is active. Scheduled on the engine frame clock:
   *  fires die with the activation, respect era weight + the paused clock. */
  drops?: Drop[];
  /** Optional member interleaving (A12, the BotW footstep principle): minimum
   *  seconds between ANY two drop fires of this bed; a blocked drop re-rolls a
   *  short deferral instead of piling on. Off when absent. */
  minSpacingSec?: number;
  // ── clip fields (N1 / N10 — kind:'clip' only) ────────────────────────────
  /** How the clip plays (N10 — the merged cue/clip citizen). Default 'sync'.
   *   - 'sync'    : clock-locked region (N1) — plays when the playhead is inside
   *                 [t, t+dur], scrub-seeks, stops on leave/pause/era.
   *   - 'trigger' : edge-fires once when the playhead crosses `t` going forward
   *                 (the old cue), then plays out fire-and-forget; honours
   *                 vary (chance/oneOf/jitter/detune). `dur` is the sound's
   *                 natural length (for the bar); it does NOT gate firing. */
  playback?: 'sync' | 'trigger';
  /** N7 — script⇄performer arbitration when this clip collides with the
   *  visitor's live performance (a cassette riding the music slot):
   *   - 'force'  (default when absent) play regardless, at full level.
   *   - 'duck'   play, dipping the visitor's channel (music) while this sounds.
   *   - 'skip'   don't play while the visitor's tape rides (let their moment be).
   *   - 'defer'  hold while the tape rides; play once it clears (if still due).
   *  ⚠ sound-changing — the per-clip default + the defer window + the eject-
   *  restore feel are John's calls (the design register the corrected model opens). */
  arbitration?: 'defer' | 'duck' | 'skip' | 'force';
  /** Region start, day units 0..1 (where the region sits on the timeline; for a
   *  'trigger' clip, the moment it fires). */
  t?: number;
  /** Region length in REAL SECONDS (the day is dayLengthSeconds long; the
   *  engine maps day-t ↔ seconds). A DAW region, not a day-fraction. */
  dur?: number;
  /** Source trim (seconds): playback inside the region runs the source window
   *  [in, out]. For a sample this is the cut; for a synth asset the voice is
   *  generated to length and in/out are ignored. */
  in?: number;
  out?: number;
  /** Edge fades in seconds (region entry / exit / pause). */
  fadeIn?: number;
  fadeOut?: number;
  // ── music fields (N4 — kind:'music' only) ────────────────────────────────
  /** What an era edge does to this track: 'restart' (default — from 0:00,
   *  the original behavior, now chosen instead of hidden) or 'continue'
   *  (remember per-track position; resume where it left off). */
  resume?: 'restart' | 'continue';
  /** The generator gate (N8): this placement is inert until the session's
   *  one-way `powered` latch is set — the shop's electrical voice waits for
   *  the pull-start. Level-triggered beds fade in on their own when power
   *  arrives (fluid, no hard cut). */
  requiresPower?: boolean;
}

/** Continuous era weight for the crossfade: 'both' is always full; past/present
 *  fade with the morph (0 = past, 1 = present). */
export function eraWeight(scope: StateTag | undefined, morph: number): number {
  if (!scope || scope === 'both') return 1;
  return scope === 'present' ? morph : 1 - morph;
}

// Beds sit in space differently: a generator carries across the yard (area);
// most fixtures are tight point sources. Exported so the AMBIENCE lane picks
// the same default when a bed is dropped onto a hero.
export function spatialForHero(heroId: string): SpatialType {
  return heroId.includes('generator') ? 'area' : 'point';
}

/** Derive bed + music placements from the per-era states.json config — what the
 *  SoundscapeEngine plays in place of the retired buildStateRules, but
 *  level-triggered with an era crossfade and diegetic music from the boombox. */
export function placementsFromStateConfig(cfg: StateConfig): SoundPlacement[] {
  const out: SoundPlacement[] = [];
  for (const era of ['past', 'present'] as const) {
    const e = cfg[era];
    if (e.music) {
      out.push({
        id: `music_${era}`, kind: 'music', asset: e.music, scope: era,
        channel: 'music', at: { heroId: 'hero_boombox' }, spatial: 'area',
        gain: e.musicVolume ?? 0.6, loop: true,
      });
    }
    for (const [heroId, bed] of Object.entries(e.heroAudio ?? {})) {
      out.push({
        id: `bed_${era}_${heroId}`, kind: 'bed', asset: bed.id, scope: era,
        channel: bed.channel ?? 'ambient', at: { heroId }, spatial: spatialForHero(heroId),
        gain: bed.volume ?? 0.4, span: [0, 1], loop: bed.loop ?? true,
      });
    }
  }
  return out;
}
